import {
  Directive,
  ElementRef,
  HostBinding,
  inject,
  input,
  signal,
  effect,
} from '@angular/core';

export type ButtonGroupItemPosition = 'first' | 'middle' | 'last' | 'only';

@Directive({
  selector: '[appM3ButtonGroupItem]',
  standalone: true,
  host: {
    '[attr.data-button-group-item]': 'true',
    '[attr.aria-disabled]': 'disabled() || null',
  },
})
export class M3ButtonGroupItemDirective {
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  value = input<unknown>();
  disabled = input<boolean>(false);

  tabIndex = signal<number>(-1);
  position = signal<ButtonGroupItemPosition>('only');

  constructor() {
    effect(() => {
      const tabIndexValue = this.tabIndex();
      const buttonEl = this.getInnerButton();
      if (buttonEl) {
        buttonEl.setAttribute('tabindex', String(tabIndexValue));
      }
    });
  }
  isSelected = signal<boolean>(false);

  @HostBinding('class.button-group-item-first')
  get isFirst(): boolean {
    return this.position() === 'first';
  }

  @HostBinding('class.button-group-item-middle')
  get isMiddle(): boolean {
    return this.position() === 'middle';
  }

  @HostBinding('class.button-group-item-last')
  get isLast(): boolean {
    return this.position() === 'last';
  }

  @HostBinding('class.button-group-item-only')
  get isOnly(): boolean {
    return this.position() === 'only';
  }

  @HostBinding('class.button-group-item-selected')
  get selected(): boolean {
    return this.isSelected();
  }

  setTabIndex(value: number): void {
    this.tabIndex.set(value);
  }

  setPosition(pos: ButtonGroupItemPosition): void {
    this.position.set(pos);
  }

  setSelected(selected: boolean): void {
    this.isSelected.set(selected);
  }

  focus(): void {
    const buttonEl = this.getInnerButton();
    if (buttonEl) {
      buttonEl.focus();
    } else {
      this.elementRef.nativeElement.focus();
    }
  }

  click(): void {
    const buttonEl = this.getInnerButton();
    if (buttonEl) {
      buttonEl.click();
    } else {
      this.elementRef.nativeElement.click();
    }
  }

  private getInnerButton(): HTMLButtonElement | null {
    return this.elementRef.nativeElement.querySelector('button');
  }
}
